import React from "react";
import { NormalText, RainbowBorder } from "../../../../common/native";
import {
  CropRotate,
  Gallery,
  SwapHorizontal,
  Text as TextIcon,
  Swap,
} from "../../../../../assets";
import {
  View,
  TouchableOpacity,
  StyleSheet,
  FlatList,
  Animated,
  Easing,
} from "react-native";
import { TCropCoords } from "../../../../../utils/types";
import { TSideBySideProcessedImage } from "../../../../../services/api-clinic";
import { useOnboardingTips } from "../../../../../contexts/OnboardingTipsContext";
import { TOOLTIP_STEPS } from "../../../../../utils/constant";

const openModalOptions = [
  { label: "Text", value: "add_text", icon: <TextIcon /> },
  { label: "Image", value: "add_image", icon: <Gallery /> },
  { label: "Adjust", value: "adjust_template", icon: <CropRotate /> },
  { label: "Swap", value: "swap_image", icon: <Swap /> },
  { label: "Unpaired", value: "unpaired", icon: <SwapHorizontal /> },
];

type OpenModalButtonsProps = {
  disabled: boolean;
  tipsEnabled?: boolean;
  hasUnpairedImages?: boolean;
  handlePress: (
    value:
      | "add_text"
      | "add_image"
      | "adjust_template"
      | "swap_image"
      | "unpaired"
      | null,
  ) => void;
  swapImagesModalData: {
    handleSetAfterCroppedImage: (
      image: string,
      index: number,
      updatedCropCoords: TCropCoords,
      selectedImage?: TSideBySideProcessedImage,
      rotationDegrees?: number,
    ) => void;
    handleSetBeforeCroppedImage: (
      image: string,
      index: number,
      updatedCropCoords: TCropCoords,
      selectedImage?: TSideBySideProcessedImage,
      rotationDegrees?: number,
    ) => void;
    handleSetBothCroppedImages?: (
      beforeImage: string,
      afterImage: string,
      index: number,
      beforeCropCoords: TCropCoords,
      afterCropCoords: TCropCoords,
      beforeRotationDegrees?: number,
      afterRotationDegrees?: number,
    ) => void;
    templatesData: any[];
    currentTemplateIndex: number;
    uploadedImages: { preOp: Blob[]; postOp: Blob[] };
    processedImageUrls: {
      preOp: string[];
      postOp: string[];
    };
  } | null;
};

const OpenModalButtons: React.FC<OpenModalButtonsProps> = ({
  disabled,
  tipsEnabled = false,
  hasUnpairedImages = false,
  handlePress,
  swapImagesModalData,
}) => {
  const { tipsState, markStepComplete } = useOnboardingTips();
  const pulseScale = React.useRef(new Animated.Value(1)).current;
  const pulseAnimationRef = React.useRef<Animated.CompositeAnimation | null>(
    null,
  );

  React.useEffect(() => {
    pulseAnimationRef.current?.stop();

    if (hasUnpairedImages) {
      const animation = Animated.loop(
        Animated.sequence([
          Animated.timing(pulseScale, {
            toValue: 1.06,
            duration: 1000,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true,
          }),
          Animated.timing(pulseScale, {
            toValue: 1,
            duration: 1000,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true,
          }),
        ]),
      );
      pulseAnimationRef.current = animation;
      animation.start();
      return () => animation.stop();
    }

    pulseScale.setValue(1);
  }, [hasUnpairedImages, pulseScale]);

  return (
    <View style={{ gap: 10, alignItems: "center" }}>
      <FlatList
        data={openModalOptions.filter(
          (item) =>
            swapImagesModalData !== null ||
            (item.value !== "adjust_template" &&
              item.value !== "swap_image" &&
              item.value !== "unpaired"),
        )}
        horizontal
        keyExtractor={(item) => item.label}
        renderItem={({ item }) => {
          const isAdjust = item.value === "adjust_template";
          const isSwap = item.value === "swap_image";
          const isUnpaired = item.value === "unpaired";

          const isActive =
            (isAdjust && tipsState.adjustImages) ||
            (isSwap && tipsState.swapImages);

          const isOnboardingHighlighted =
            tipsEnabled &&
            ((isAdjust && tipsState.adjustImages && !tipsState.swapImages) ||
              (isSwap && tipsState.swapImages && !tipsState.saveChanges));

          const onPress = () => {
            handlePress(item.value as any);

            if (isAdjust && tipsState.adjustImages && !tipsState.swapImages) {
              markStepComplete(TOOLTIP_STEPS.ADJUST_IMAGES);
            } else if (
              isSwap &&
              tipsState.swapImages &&
              !tipsState.saveChanges
            ) {
              markStepComplete(TOOLTIP_STEPS.SWAP_IMAGES);
            }
          };

          const button = (
            <TouchableOpacity
              disabled={disabled}
              style={[
                styles.option,
                isUnpaired &&
                  hasUnpairedImages &&
                  styles.unpairedNeedsAttention,
              ]}
              onPress={onPress}
            >
              <View style={styles.iconContainer}>{item.icon}</View>
              <NormalText text={item.label} style={styles.optionText} />
            </TouchableOpacity>
          );

          const buttonWithAnimation =
            isUnpaired && hasUnpairedImages ? (
              <Animated.View style={{ transform: [{ scale: pulseScale }] }}>
                {button}
              </Animated.View>
            ) : (
              button
            );

          return isOnboardingHighlighted ? (
            <RainbowBorder key={item.label} isActive borderRadius={6}>
              <View style={styles.optionWrapper}>{buttonWithAnimation}</View>
            </RainbowBorder>
          ) : (
            <View key={item.label} style={styles.optionWrapper}>
              {buttonWithAnimation}
            </View>
          );
        }}
        showsHorizontalScrollIndicator
        contentContainerStyle={styles.container}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: 10,
  },
  option: {
    borderRadius: 5,
    borderWidth: 1,
    borderColor: "#C4C6CD",
    paddingHorizontal: 5,
    paddingVertical: 3,
    width: 72,
    height: 50,
    justifyContent: "flex-start",
    alignItems: "center",
  },
  optionWrapper: {
    padding: 3,
  },
  unpairedNeedsAttention: {
    borderWidth: 2,
    borderColor: "#374151",
  },
  optionText: {
    fontSize: 10,
    textAlign: "center",
    fontWeight: "600",
  },
  iconContainer: {
    justifyContent: "center",
    alignItems: "center",
    width: 40,
    height: 21,
  },
});

export default OpenModalButtons;
